import { Button } from '@bia-auction/ui';
import { t } from '@bia-auction/shared-i18n';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatCount } from '../lib/format';

/**
 * Offset-pagination controls for the `{ items, total }` list envelope (same component as
 * web-admin's — the apps vendor their own component trees, the `pagination.*` i18n keys are
 * shared): prev/next plus a "from–to / total" range readout. `offset`/`limit` mirror the server
 * query params; the caller owns the state (usually per query key) and re-fetches on change.
 */
export function Pagination({
  offset,
  limit,
  total,
  onOffsetChange,
}: {
  offset: number;
  limit: number;
  total: number;
  onOffsetChange: (offset: number) => void;
}) {
  if (total <= 0) return null;
  const from = offset + 1;
  const to = Math.min(offset + limit, total);
  return (
    <div className="flex items-center justify-end gap-2 text-sm text-muted-foreground">
      <span>
        {/* Counts follow the AGENTS.md number format (`1.234`), not raw String() digits. */}
        {t('pagination.range', {
          from: formatCount(from),
          to: formatCount(to),
          total: formatCount(total),
        })}
      </span>
      <Button
        variant="ghost"
        size="sm"
        title={t('pagination.prev')}
        disabled={offset <= 0}
        onClick={() => onOffsetChange(Math.max(0, offset - limit))}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        title={t('pagination.next')}
        disabled={to >= total}
        onClick={() => onOffsetChange(offset + limit)}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}
